import Phaser from 'phaser';
import { COLORS, FONT_MONO } from '../style.js';

/**
 * Compact per-player dice-fairness histogram. Six vertical bars (one
 * per face 1–6) showing how often that face was rolled, overlaid with
 * a horizontal "expected" line at totalDice/6 — the uniform-baseline
 * the dice would hit if perfectly fair.
 *
 * Bars are tinted with the seat color so each player's histogram is
 * visually distinct. The expected line stays a muted cross-cut so it
 * reads as "reference" not "this is the value."
 *
 * Drawn as a single Graphics object plus a few Text labels, all
 * mounted into the supplied parent container so the caller controls
 * scroll position / scene shutdown via the container.
 */

export interface DiceFairnessChartOptions {
  readonly scene: Phaser.Scene;
  readonly parent: Phaser.GameObjects.Container;
  /** Top-left in design coords (parent-local). */
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** Length-6 array: index 0 = count of 1s, ..., index 5 = count of 6s. */
  readonly diceRolls: readonly number[];
  readonly totalDice: number;
  /** Seat color used to tint the bars (hex string, e.g. "#e63946"). */
  readonly colorHex: string;
  /** Render face labels (1-6) under each bar. Default true. */
  readonly showFaceLabels?: boolean;
}

const FACE_LABEL_HEIGHT = 14;
const COUNT_LABEL_HEIGHT = 12;
const BAR_GAP_RATIO = 0.35; // gap is 35% of the per-face cell width
const MIN_BAR_HEIGHT = 2;   // visible nub even for 0-count faces

/** Returns the chart's bottom edge (for caller layout flow). */
export function drawDiceFairnessChart(
  opts: DiceFairnessChartOptions
): { height: number } {
  const {
    scene,
    parent,
    x,
    y,
    width,
    height,
    diceRolls,
    totalDice,
    colorHex,
    showFaceLabels = true,
  } = opts;

  const colorInt = Phaser.Display.Color.HexStringToColor(colorHex).color;

  // Vertical budget: room for the count label above each bar and the
  // face label below. The bar area is what's left.
  const labelTopReserve = COUNT_LABEL_HEIGHT + 2;
  const labelBottomReserve = showFaceLabels ? FACE_LABEL_HEIGHT + 2 : 0;
  const barAreaTop = y + labelTopReserve;
  const barAreaBottom = y + height - labelBottomReserve;
  const barAreaHeight = Math.max(8, barAreaBottom - barAreaTop);

  // Per-face cell width + bar width.
  const cellWidth = width / 6;
  const barWidth = cellWidth * (1 - BAR_GAP_RATIO);

  // Max count drives the bar-height scale. If everything is 0 the
  // chart is just labels — degenerate but won't crash.
  const maxCount = Math.max(1, ...diceRolls);

  // Expected count per face if the RNG were fair: totalDice / 6.
  const expected = totalDice / 6;
  const expectedY =
    barAreaBottom - (expected / maxCount) * barAreaHeight;

  // Single Graphics for bars + baseline.
  const g = scene.add.graphics();

  // Draw bars first (so the baseline sits on top).
  for (let face = 1; face <= 6; face++) {
    const count = diceRolls[face - 1] ?? 0;
    const cx = x + (face - 1) * cellWidth + cellWidth / 2;
    const barLeft = cx - barWidth / 2;
    const barH = Math.max(
      MIN_BAR_HEIGHT,
      (count / maxCount) * barAreaHeight
    );
    const barTop = barAreaBottom - barH;

    // Tint bars by seat color, slightly darker fill, brighter top edge.
    g.fillStyle(colorInt, 0.78);
    g.fillRect(barLeft, barTop, barWidth, barH);
    g.lineStyle(1, colorInt, 1);
    g.strokeRect(barLeft, barTop, barWidth, barH);
  }

  // Expected-uniform baseline — muted dashed-effect via short
  // line segments so it reads as "reference" not "another bar".
  if (totalDice > 0 && expected > 0) {
    g.lineStyle(1, COLORS.textMuted as unknown as number, 0.85);
    // textMuted is a hex string, not int — convert.
    const mutedInt = Phaser.Display.Color.HexStringToColor(
      COLORS.textMuted
    ).color;
    g.lineStyle(1, mutedInt, 0.9);
    // Dashed line — 8px on, 5px off across the bar area.
    const dashOn = 8;
    const dashOff = 5;
    let cursor = x;
    while (cursor < x + width) {
      const segEnd = Math.min(cursor + dashOn, x + width);
      g.lineBetween(cursor, expectedY, segEnd, expectedY);
      cursor = segEnd + dashOff;
    }
  }

  parent.add(g);

  // Count labels above each bar (small mono numbers). Skip when
  // the chart is very compact — labels here would crowd the bars.
  if (height >= 60) {
    for (let face = 1; face <= 6; face++) {
      const count = diceRolls[face - 1] ?? 0;
      const cx = x + (face - 1) * cellWidth + cellWidth / 2;
      const label = scene.add
        .text(cx, y + labelTopReserve / 2, String(count), {
          fontFamily: FONT_MONO,
          fontSize: '10px',
          color: COLORS.textMuted,
        })
        .setOrigin(0.5, 0.5);
      parent.add(label);
    }
  }

  // Face labels (1-6) below each bar.
  if (showFaceLabels) {
    for (let face = 1; face <= 6; face++) {
      const cx = x + (face - 1) * cellWidth + cellWidth / 2;
      const label = scene.add
        .text(cx, barAreaBottom + 4, String(face), {
          fontFamily: FONT_MONO,
          fontSize: '11px',
          color: COLORS.textSecondary,
        })
        .setOrigin(0.5, 0);
      parent.add(label);
    }
  }

  return { height };
}
